import { ref } from 'vue'
import {
  splitPdfByRanges,
  splitPdfIntoPages,
  fileToArrayBuffer,
  getPdfPageCount,
} from '@/utils/pdfSplit'
import { mergePdfs, getTotalPageCount } from '@/utils/pdfMerge'
import {
  isPdfFile,
  isValidFileSize,
  formatFileSize,
  validatePageRanges,
  parsePageRanges,
} from '@/utils/validation'

export function usePdfHandler() {
  const loading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Validate PDF file
   */
  const validatePdfFile = (file: File): { valid: boolean; error?: string } => {
    if (!isPdfFile(file)) {
      return { valid: false, error: 'Please select a valid PDF file' }
    }

    if (!isValidFileSize(file, 50)) {
      return { valid: false, error: 'File size exceeds 50MB limit' }
    }

    return { valid: true }
  }

  /**
   * Load PDF and get page count
   */
  const loadPdfInfo = async (file: File): Promise<{ pageCount: number; size: string }> => {
    try {
      const arrayBuffer = await fileToArrayBuffer(file)
      const pageCount = await getPdfPageCount(arrayBuffer)
      const size = formatFileSize(file.size)

      return { pageCount, size }
    } catch (err) {
      throw new Error('Failed to load PDF file')
    }
  }

  /**
   * Split PDF by ranges
   */
  const splitPdf = async (
    file: File,
    ranges: string,
    splitAll: boolean = false
  ): Promise<Blob[]> => {
    loading.value = true
    error.value = null

    try {
      // Validate file
      const validation = validatePdfFile(file)
      if (!validation.valid) {
        throw new Error(validation.error)
      }

      // Validate ranges
      if (!splitAll && !validatePageRanges(ranges)) {
        throw new Error('Invalid page ranges format')
      }

      const arrayBuffer = await fileToArrayBuffer(file)
      let results: Uint8Array[]

      if (splitAll) {
        results = await splitPdfIntoPages(arrayBuffer)
      } else {
        results = await splitPdfByRanges(arrayBuffer, ranges)
      }

      // Convert to blobs
      const blobs = results.map(bytes => new Blob([bytes], { type: 'application/pdf' }))
      return blobs
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to split PDF'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Merge multiple PDFs
   */
  const mergePdf = async (files: File[]): Promise<Blob> => {
    loading.value = true
    error.value = null

    if (files.length < 2) {
      error.value = 'Please select at least 2 PDF files'
      throw new Error(error.value)
    }

    try {
      // Validate all files
      for (const file of files) {
        const validation = validatePdfFile(file)
        if (!validation.valid) {
          throw new Error(validation.error)
        }
      }

      // Convert files to array buffers
      const arrayBuffers = await Promise.all(
        files.map(file => fileToArrayBuffer(file))
      )

      // Merge
      const mergedBytes = await mergePdfs(arrayBuffers)
      return new Blob([mergedBytes], { type: 'application/pdf' })
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to merge PDFs'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Download PDF blob
   */
  const downloadPdf = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  /**
   * Download multiple PDFs as individual files
   */
  const downloadMultiplePdfs = async (blobs: Blob[], baseName: string) => {
    for (let i = 0; i < blobs.length; i++) {
      const filename = `${baseName}_part_${i + 1}.pdf`
      downloadPdf(blobs[i], filename)
      // Add small delay to avoid browser blocking multiple downloads
      await new Promise(resolve => setTimeout(resolve, 200))
    }
  }

  return {
    loading,
    error,
    validatePdfFile,
    loadPdfInfo,
    splitPdf,
    mergePdf,
    downloadPdf,
    downloadMultiplePdfs,
  }
}
